// src/components/admin/SystemHealth.jsx
// Panel de salud del bot/sistema. Datos en vivo desde RTDB:
//   /live_games   → cantidad de partidos activos + edad del último update
//   /signals_history → última señal y timestamp para inferir actividad bot

import { useEffect, useState } from 'react';
import { onValue, ref, query, limitToLast } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';

function ago(ms) {
  if (!ms) return '—';
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 5) return 'ahora';
  if (s < 60) return `hace ${s}s`;
  if (s < 3600) return `hace ${Math.floor(s/60)}m`;
  return `hace ${Math.floor(s/3600)}h`;
}

function tsToMs(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

export default function SystemHealth() {
  const { isAdmin } = useAuth();
  const [liveCount, setLiveCount] = useState(null);
  const [lastLiveTs, setLastLiveTs] = useState(null);
  const [lastSignalTs, setLastSignalTs] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Test pago admin (CLP 1.000) — invoca adminCreateTestPayment en backend.
  // El payment se persiste con isTest:true; flowConfirm/userCheckPaymentStatus
  // saltean accessUntil/email para no contaminar el user doc del admin.
  const [testPaymentLoading, setTestPaymentLoading] = useState(false);
  const [testPaymentError, setTestPaymentError] = useState(null);
  const [testPaymentResult, setTestPaymentResult] = useState(null);

  async function handleTestPayment(planId = 'test_1000') {
    setTestPaymentLoading(true);
    setTestPaymentError(null);
    setTestPaymentResult(null);
    try {
      const callable = httpsCallable(functions, 'adminCreateTestPayment');
      const r = await callable({ origin: window.location.origin, planId });
      const { paymentUrl, commerceOrder } = r.data || {};
      if (!paymentUrl) {
        throw new Error('Respuesta sin paymentUrl.');
      }
      setTestPaymentResult({ paymentUrl, commerceOrder });
      // Igual que el checkout público: marca el retorno como pago único para que
      // /pago/return rutee a /pago/exito (y no caiga en la rama de suscripción).
      localStorage.setItem('ee_processing_next', 'payment_success');
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[adminCreateTestPayment]', err);
      setTestPaymentError(err.message || String(err));
    } finally {
      setTestPaymentLoading(false);
    }
  }

  // Tick cada 5s para refrescar "edad".
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(i);
  }, []);

  // /live_games — count + max(timestamp).
  useEffect(() => {
    const r = ref(db, 'live_games');
    const unsub = onValue(r, (snap) => {
      const d = snap.val() || {};
      const arr = Object.values(d).filter((g) => g && typeof g === 'object');
      setLiveCount(arr.length);
      let maxTs = 0;
      arr.forEach((g) => {
        const ts = tsToMs(g.timestamp);
        if (ts && ts > maxTs) maxTs = ts;
      });
      setLastLiveTs(maxTs || null);
    });
    return () => unsub();
  }, []);

  // /signals_history — últimas 5 entradas, max started_at.
  useEffect(() => {
    const r = query(ref(db, 'signals_history'), limitToLast(5));
    const unsub = onValue(r, (snap) => {
      const d = snap.val() || {};
      let maxTs = 0;
      Object.values(d).forEach((s) => {
        if (s && typeof s.started_at === 'number' && s.started_at > maxTs) maxTs = s.started_at;
      });
      setLastSignalTs(maxTs || null);
    });
    return () => unsub();
  }, []);

  const liveAge = lastLiveTs ? Math.floor((now - lastLiveTs) / 1000) : null;
  const botStatus = liveAge !== null && liveAge < 60 ? 'ok'
                  : liveAge !== null && liveAge < 300 ? 'warn'
                  : 'err';

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Salud del sistema (en vivo)</h3>
      <div className="admin-health-grid">
        <div className="admin-health-card">
          <span className="admin-health-dot" data-status={botStatus} />
          <div>
            <div className="label">Bot edge-elite</div>
            <div className="value">
              {botStatus === 'ok' ? 'Activo' : botStatus === 'warn' ? 'Lento' : 'Sin actividad reciente'}
            </div>
            <div className="sub">Último tick a /live_games: {ago(lastLiveTs)}</div>
          </div>
        </div>

        <div className="admin-health-card">
          <span className="admin-health-dot" data-status={liveCount === null ? 'warn' : 'ok'} />
          <div>
            <div className="label">/live_games</div>
            <div className="value">{liveCount === null ? '—' : `${liveCount} partido${liveCount === 1 ? '' : 's'}`}</div>
            <div className="sub">Realtime via Firebase RTDB</div>
          </div>
        </div>

        <div className="admin-health-card">
          <span className="admin-health-dot" data-status={lastSignalTs ? 'ok' : 'warn'} />
          <div>
            <div className="label">Última señal Telegram</div>
            <div className="value">{ago(lastSignalTs)}</div>
            <div className="sub">Listener en /signals_history</div>
          </div>
        </div>

        <div className="admin-health-card">
          <span className="admin-health-dot" data-status="ok" />
          <div>
            <div className="label">Firebase RTDB</div>
            <div className="value">Conectado</div>
            <div className="sub">onValue subscriptions activas</div>
          </div>
        </div>

        <div className="admin-health-card">
          <span className="admin-health-dot" data-status="warn" />
          <div>
            <div className="label">Cloud Functions</div>
            <div className="value">No monitoreadas</div>
            <div className="sub">flowConfirm / telegramBindCode (sin healthcheck)</div>
          </div>
        </div>

        <div className="admin-health-card">
          <span className="admin-health-dot" data-status="warn" />
          <div>
            <div className="label">EC2 (bot)</div>
            <div className="value">Sin healthcheck local</div>
            <div className="sub">Ver via SSH journalctl -u edge-elite-bot</div>
          </div>
        </div>
      </div>

      {/* Test pago admin (CLP 1.000). Defensa en profundidad: el panel ya
          tiene AdminProtected, pero ocultamos también si !isAdmin para que
          no aparezca si el componente se montara fuera del contexto admin. */}
      {isAdmin && (
        <div style={{
          marginTop: 24,
          padding: 16,
          border: '1px dashed #d33',
          borderRadius: 10,
          background: 'rgba(220, 51, 51, 0.04)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#d33', marginBottom: 4 }}>
            🧪 Pruebas de pago (admin)
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.45 }}>
            Crea un pago real en Flow producción por CLP 1.000 a tu nombre con
            flag <code>isTest:true</code>. El webhook NO actualiza tu accessUntil
            ni envía email. Sirve para validar la integración end-to-end con el
            comercio productivo sin contaminar tu cuenta.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => handleTestPayment('test_500')}
              disabled={testPaymentLoading}
              style={{
                padding: '10px 16px',
                background: testPaymentLoading ? '#999' : '#d33',
                color: '#fff', border: 0, borderRadius: 8, fontSize: 13,
                fontWeight: 600, cursor: testPaymentLoading ? 'wait' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {testPaymentLoading ? 'Generando…' : '🧪 Test pago CLP 500 (admin only)'}
            </button>
            <button
              type="button"
              onClick={() => handleTestPayment('test_1000')}
              disabled={testPaymentLoading}
              style={{
                padding: '10px 16px',
                background: testPaymentLoading ? '#999' : '#b91c1c',
                color: '#fff', border: 0, borderRadius: 8, fontSize: 13,
                fontWeight: 600, cursor: testPaymentLoading ? 'wait' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {testPaymentLoading ? 'Generando…' : '🧪 Test pago CLP 1.000'}
            </button>
          </div>
          {testPaymentResult && (
            <div style={{
              marginTop: 10,
              fontSize: 11,
              color: 'var(--muted)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1.5,
            }}>
              ✓ Orden creada: <strong style={{ color: 'var(--ink)' }}>{testPaymentResult.commerceOrder}</strong>
              <br />
              Si la pestaña no abrió:{' '}
              <a href={testPaymentResult.paymentUrl} target="_blank" rel="noopener noreferrer">
                abrir manualmente
              </a>
            </div>
          )}
          {testPaymentError && (
            <div style={{
              marginTop: 10,
              padding: '8px 10px',
              background: 'rgba(220, 51, 51, 0.1)',
              border: '1px solid #d33',
              borderRadius: 6,
              fontSize: 11,
              color: '#d33',
              fontFamily: 'var(--font-mono)',
            }}>
              Error: {testPaymentError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
